import { ArrowLeft } from 'lucide-react'
import { navigate } from '../../lib/router'
import { useAuthStore } from '../../lib/auth-store'
import { PublicHeader } from '../../components/marketing/PublicHeader'
import { Footer } from '../../components/Footer'
import { Button } from '../../components/ui/button'

const h2 = 'text-2xl font-semibold tracking-tight'
const list = 'list-disc space-y-1 pl-5'
const link = 'text-primary underline underline-offset-4'

export function TermsPage() {
  const accessToken = useAuthStore((s) => s.accessToken)
  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground">
      <PublicHeader loggedIn={!!accessToken} />
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 pt-32 pb-24">
        <article className="space-y-6 text-sm leading-relaxed text-foreground/90 md:text-base">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="-ml-2 text-muted-foreground">
            <ArrowLeft /> Back
          </Button>

          <header className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Terms of Service</h1>
            <p className="text-xs text-muted-foreground">Last updated: April 2026</p>
          </header>

          <section className="space-y-2">
            <h2 className={h2}>1. Provider</h2>
            <p>
              These terms govern your use of the Doable platform ("Service"), operated by
              Applox GmbH, [ADDRESS], Germany ("we", "us", "Applox").
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>2. Service description</h2>
            <p>
              Doable is a platform that generates web applications from natural-language prompts
              using AI (Anthropic Claude). The Service includes project management, code
              generation, deployment to preview URLs, and workspace collaboration tools.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>3. Your Anthropic API key (BYO key model)</h2>
            <p>
              To use the code generation features, you must provide your own Anthropic API key
              (or AWS Bedrock / GCP Vertex credentials). <strong>We do not provide, resell, or
              subsidise AI inference.</strong> Your API key is used exclusively to run Claude
              on your behalf. Anthropic bills you directly through your own account.
            </p>
            <ul className={`${list} mt-2`}>
              <li>Your API key is encrypted at rest (AES-256-GCM) and never stored in plaintext.</li>
              <li>It is injected into the build subprocess as an ephemeral environment variable and discarded after each build.</li>
              <li>We never log, inspect, or transmit your key to any system other than the Anthropic API.</li>
              <li>You are responsible for your Anthropic API key, its security, and any costs incurred through its use.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>4. Intellectual property</h2>
            <p>
              <strong>Code generated by Doable belongs to you.</strong> We claim no intellectual
              property rights over the output of your builds. You may use, modify, distribute,
              and commercially exploit all generated code without restriction from us.
            </p>
            <p className="mt-2">
              The Doable platform itself (UI, control plane, worker, infrastructure) is the
              intellectual property of Applox GmbH. Your right to use it is limited to the
              scope of your subscription or trial.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>5. No warranty on generated code</h2>
            <p>
              Generated code is provided <strong>as-is</strong>. We do not guarantee that it is
              free of bugs, security vulnerabilities, performance issues, or license violations.
              <strong> You are responsible for reviewing, testing, and securing all generated code
              before deploying it to production.</strong>
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>6. Acceptable use</h2>
            <p>You agree not to use Doable to:</p>
            <ul className={list}>
              <li>Generate malware, ransomware, or code designed to cause harm.</li>
              <li>Generate code that infringes third-party intellectual property.</li>
              <li>Circumvent security controls, authentication systems, or access restrictions.</li>
              <li>Abuse the Anthropic API or violate{' '}
                <a href="https://www.anthropic.com/policies/aup" target="_blank" rel="noreferrer" className={link}>
                  Anthropic's Acceptable Use Policy
                </a>.
              </li>
              <li>Attempt to access other users' workspaces, projects, or data.</li>
              <li>Use the platform to perform denial-of-service attacks or excessive automated requests.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>7. Account and access</h2>
            <ul className={list}>
              <li>You must provide accurate information at registration.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>Notify us immediately at security@doable.website if you suspect unauthorised access.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>8. Service availability</h2>
            <p>
              We aim for high availability but do not guarantee uninterrupted service. The
              platform is provided on a <strong>best-effort</strong> basis. We reserve the right
              to perform maintenance, updates, or temporary shutdowns with reasonable notice.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>9. Data and privacy</h2>
            <p>
              Your data is handled in accordance with our{' '}
              <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy') }} className={link}>
                Privacy Policy
              </a>.
              By using the Service, you acknowledge and agree to the data processing described
              therein.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>10. Termination</h2>
            <ul className={list}>
              <li>Either party may terminate with 30 days' written notice.</li>
              <li>We may suspend or terminate your access immediately for violations of these terms.</li>
              <li>On termination: you may export your project files within 30 days. After that, we delete your workspace data.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>11. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, Applox GmbH's total liability under or in
              connection with these terms shall not exceed the total fees paid by you in the 12
              months preceding the claim, or €10,000, whichever is greater. We are not liable for
              indirect, consequential, or incidental damages, including lost profits, data loss,
              or business interruption.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>12. Governing law and jurisdiction</h2>
            <p>
              These terms are governed by the laws of the Federal Republic of Germany. The
              exclusive jurisdiction for disputes arising from or in connection with these terms
              is the courts of [CITY], Germany.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>13. Changes</h2>
            <p>
              We may update these terms from time to time. Material changes will be communicated
              via email or an in-app notice at least 14 days before taking effect. Continued use
              after the effective date constitutes acceptance.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>14. Contact</h2>
            <p>
              Applox GmbH<br />
              Email: hello@doable.website<br />
              [ADDRESS]
            </p>
          </section>
        </article>
      </main>
      <Footer variant="site" />
    </div>
  )
}
